import React, { useContext, useEffect, useState } from 'react'
import {
  Paper, Button, IconButton, Switch, FormControlLabel,
  Snackbar, Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress, useTheme, Collapse, Tooltip
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import StarIcon from '@mui/icons-material/Star'
import StarBorderIcon from '@mui/icons-material/StarBorder'
import PaymentIcon from '@mui/icons-material/Payment'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import LinkOffIcon from '@mui/icons-material/LinkOff'
import DescriptionIcon from '@mui/icons-material/Description'
import hipersendService from '../../services/hipersend'
import TemplateGenerator from './TemplateGenerator'
import BannerManager from './BannerManager'
import { ApiDataWrapperContext } from '../../contexts/ApiDataWrapper'
import Text from '../Text'
import TextFieldCustom from '../TextFieldCustom'

const EMPTY_FORM = {
  name: '',
  title: '',
  subtitle: '',
  footerTitle: '',
  footerSubtitle: '',
  logoUrl: '',
  logoWidth: '180px',
  primaryColor: '#03adec',
  info: '',
  isDefault: false
}

export default function PaymentProfiles() {
  const theme = useTheme()
  const { getCredentials } = useContext(ApiDataWrapperContext)
  const { api, secret } = getCredentials()

  const [hiperpayClientId, setHiperpayClientId] = useState(null)
  const [hiperpayClientSecret, setHiperpayClientSecret] = useState(null)
  const [isConnected, setIsConnected] = useState(false)
  const [setupLoading, setSetupLoading] = useState(false)
  const [initialLoading, setInitialLoading] = useState(true)
  const [localHasAccess, setLocalHasAccess] = useState(null)

  const [profiles, setProfiles] = useState([])
  const [loading, setLoading] = useState(false)
  const [profileMode, setProfileMode] = useState(false)

  const [formOpen, setFormOpen] = useState(false)
  const [editingProfile, setEditingProfile] = useState(null)
  const [form, setForm] = useState(EMPTY_FORM)
  const [saving, setSaving] = useState(false)

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [profileToDelete, setProfileToDelete] = useState(null)
  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false)
  const [templateGeneratorOpen, setTemplateGeneratorOpen] = useState(false)

  const [message, setMessage] = useState('')
  const [showCard, setShowCard] = useState(false)

  useEffect(() => {
    checkHiperPayStatus()
    const timer = setTimeout(() => setShowCard(true), 300)
    return () => clearTimeout(timer)
  }, [])

  const checkHiperPayStatus = async () => {
    try {
      if (!api || !secret) return
      const info = await hipersendService.notificacoesInteligentes.getInformations(api, secret)
      const hasHiperPayFunctionality = info?.user?.plan?.functionalities?.includes('HiperPay') || false
      setLocalHasAccess(hasHiperPayFunctionality)
      if (!hasHiperPayFunctionality) {
        setInitialLoading(false)
        return
      }
      if (info.hiperpayClientId && info.hiperpayClientSecret) {
        setHiperpayClientId(info.hiperpayClientId)
        setHiperpayClientSecret(info.hiperpayClientSecret)
        await connectToHiperPay(info.hiperpayClientId, info.hiperpayClientSecret)
      }
    } catch (error) {
      console.error(error)
      setLocalHasAccess(false)
    } finally {
      setInitialLoading(false)
    }
  }

  const connectToHiperPay = async (cId, cSecret) => {
    try {
      const settings = await hipersendService.hiperpay.getSettings(cId, cSecret)
      setProfileMode(settings.features?.profileMode || false)
      setIsConnected(true)
      await fetchProfiles(cId, cSecret)
    } catch (error) {
      console.error(error)
      setMessage('Erro ao conectar ao HiperPay')
    }
  }

  const handleSetupHiperPay = async () => {
    setSetupLoading(true)
    try {
      const result = await hipersendService.notificacoesInteligentes.setupHiperPay(api, secret)
      setHiperpayClientId(result.hiperpayClientId)
      setHiperpayClientSecret(result.hiperpayClientSecret)
      await connectToHiperPay(result.hiperpayClientId, result.hiperpayClientSecret)
      setMessage('HiperPay ativado com sucesso')
    } catch (error) {
      console.error(error)
      setMessage('Erro ao ativar HiperPay')
    } finally {
      setSetupLoading(false)
    }
  }

  const handleDisconnectHiperPay = async () => {
    try {
      await hipersendService.notificacoesInteligentes.disconnectHiperPay(api, secret)
      setHiperpayClientId(null)
      setHiperpayClientSecret(null)
      setIsConnected(false)
      setProfiles([])
      setProfileMode(false)
      setDisconnectDialogOpen(false)
      setMessage('HiperPay desconectado')
    } catch (error) {
      console.error(error)
      setMessage('Erro ao desconectar')
    }
  }

  const fetchProfiles = async (cId, cSecret) => {
    setLoading(true)
    try {
      const data = await hipersendService.hiperpay.getProfiles(cId || hiperpayClientId, cSecret || hiperpayClientSecret)
      setProfiles(data)
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const handleToggleProfileMode = async () => {
    try {
      const newValue = !profileMode
      await hipersendService.hiperpay.updateSettings(hiperpayClientId, hiperpayClientSecret, { profileMode: newValue })
      setProfileMode(newValue)
      setMessage(newValue ? 'Modo profile ativado' : 'Modo profile desativado')
    } catch (error) {
      console.error(error)
      setMessage('Erro ao atualizar configuracao')
    }
  }

  const copyProfileId = (profile) => {
    navigator.clipboard.writeText(profile._id)
    setMessage(`ID copiado: ${profile._id}`)
  }

  const openCreateForm = () => {
    setEditingProfile(null)
    setForm(EMPTY_FORM)
    setFormOpen(true)
  }

  const openEditForm = (profile) => {
    setEditingProfile(profile)
    setForm({
      name: profile.name || '',
      title: profile.title || '',
      subtitle: profile.subtitle || '',
      footerTitle: profile.footerTitle || '',
      footerSubtitle: profile.footerSubtitle || '',
      logoUrl: profile.logoUrl || '',
      logoWidth: profile.logoWidth || '180px',
      primaryColor: profile.primaryColor || '#03adec',
      info: profile.info || '',
      isDefault: profile.isDefault || false
    })
    setFormOpen(true)
  }

  const handleSave = async () => {
    if (!form.name.trim()) {
      setMessage('Nome e obrigatorio')
      return
    }
    setSaving(true)
    try {
      if (editingProfile) {
        await hipersendService.hiperpay.updateProfile(hiperpayClientId, hiperpayClientSecret, editingProfile._id, form)
        setMessage('Profile atualizado')
      } else {
        await hipersendService.hiperpay.createProfile(hiperpayClientId, hiperpayClientSecret, form)
        setMessage('Profile criado')
      }
      setFormOpen(false)
      await fetchProfiles()
    } catch (error) {
      console.error(error)
      const details = error.response?.data?.details
      setMessage(details ? details.join(', ') : 'Erro ao salvar profile')
    } finally {
      setSaving(false)
    }
  }

  const handleSetDefault = async (profile) => {
    try {
      await hipersendService.hiperpay.setDefaultProfile(hiperpayClientId, hiperpayClientSecret, profile._id)
      setMessage(`"${profile.name}" definido como padrao`)
      await fetchProfiles()
    } catch (error) {
      console.error(error)
      setMessage('Erro ao definir padrao')
    }
  }

  const handleDelete = async () => {
    if (!profileToDelete) return
    try {
      await hipersendService.hiperpay.deleteProfile(hiperpayClientId, hiperpayClientSecret, profileToDelete._id)
      setMessage('Profile removido')
      setDeleteDialogOpen(false)
      setProfileToDelete(null)
      await fetchProfiles()
    } catch (error) {
      console.error(error)
      setMessage('Erro ao remover profile')
    }
  }

  const updateField = (field) => (e) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }))
  }

  if (initialLoading) {
    return (
      <div className="px-4 md:px-12 py-12 md:py-16 flex justify-center">
        <CircularProgress />
      </div>
    )
  }

  // Redireciona pro home se o plano não inclui HiperPay. Sem isso, um usuário
  // pode acessar /hiperpay/profiles direto pela URL mesmo sem o menu aparecer.
  if (localHasAccess === false) {
    window.location.href = '/'
    return null
  }

  return (
    <div className="px-4 md:px-12 py-12 md:py-16 space-y-6">
      <div className="flex items-center space-x-3 mb-2">
        <PaymentIcon style={{ fontSize: 32, color: theme.palette.primary.main }} />
        <Text className="font-black text-2xl">HiperPay Profiles</Text>
      </div>
      <Text className="text-sm opacity-70 mb-6">
        Configure o branding das paginas de pagamento do HiperPay. Profiles permitem que o ERP envie apenas dados essenciais.
      </Text>

      {!isConnected ? (
        <Collapse in={showCard}>
          <Paper elevation={1} className="p-6 space-y-4">
            <Text className="font-medium text-lg">Ativar HiperPay</Text>
            <Text className="text-sm opacity-70">
              Ative o HiperPay para esta instancia. Uma integracao exclusiva sera criada automaticamente.
            </Text>
            <Button
              variant="contained"
              onClick={handleSetupHiperPay}
              disabled={setupLoading}
              startIcon={setupLoading ? <CircularProgress size={18} /> : <PaymentIcon />}
            >
              {setupLoading ? 'Ativando...' : 'Ativar HiperPay'}
            </Button>
          </Paper>
        </Collapse>
      ) : (
        <>
          <Paper elevation={1} className="p-4 flex items-center justify-between">
            <div>
              <Text className="font-medium">Modo Profile</Text>
              <Text className="text-sm opacity-70">
                Quando ativado, o HiperPay resolve branding a partir de profiles cadastrados.
              </Text>
            </div>
            <FormControlLabel
              control={
                <Switch
                  checked={profileMode}
                  onChange={handleToggleProfileMode}
                  color="primary"
                />
              }
              label={profileMode ? 'Ativado' : 'Desativado'}
            />
          </Paper>

          <div className="flex items-center justify-between">
            <Text className="font-medium text-lg">
              Profiles ({profiles.length})
            </Text>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreateForm}
              size="small"
            >
              Novo Profile
            </Button>
            <Button
              variant="outlined"
              startIcon={<DescriptionIcon />}
              onClick={() => setTemplateGeneratorOpen(true)}
              size="small"
            >
              Gerar Template
            </Button>
          </div>

          {loading ? (
            <div className="flex justify-center py-8">
              <CircularProgress />
            </div>
          ) : profiles.length === 0 ? (
            <Paper elevation={1} className="p-8 text-center">
              <Text className="opacity-50">Nenhum profile cadastrado</Text>
            </Paper>
          ) : (
            <div className="space-y-3">
              {profiles.map((profile) => (
                <Paper key={profile._id} elevation={1} className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      {profile.primaryColor && (
                        <div
                          className="w-8 h-8 rounded-full border-2 border-gray-300 flex-shrink-0"
                          style={{ backgroundColor: profile.primaryColor }}
                        />
                      )}
                      <div>
                        <div className="flex items-center space-x-2">
                          <Text className="font-medium">{profile.name}</Text>
                          {profile.isDefault && (
                            <span
                              className="text-xs font-bold px-2 py-0.5 rounded"
                              style={{ backgroundColor: theme.palette.primary.main, color: '#fff' }}
                            >
                              PADRAO
                            </span>
                          )}
                        </div>
                        {profile.title && (
                          <Text className="text-sm opacity-60">{profile.title}</Text>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Tooltip title={`Copiar ID para usar no template: %ProfileId%${profile._id}%/ProfileId%`}>
                        <IconButton size="small" onClick={() => copyProfileId(profile)}>
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <IconButton
                        size="small"
                        onClick={() => handleSetDefault(profile)}
                        title={profile.isDefault ? 'Profile padrao' : 'Definir como padrao'}
                      >
                        {profile.isDefault ? (
                          <StarIcon style={{ color: theme.palette.primary.main }} />
                        ) : (
                          <StarBorderIcon />
                        )}
                      </IconButton>
                      <IconButton size="small" onClick={() => openEditForm(profile)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => { setProfileToDelete(profile); setDeleteDialogOpen(true) }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </div>
                  </div>
                </Paper>
              ))}
            </div>
          )}

          <div className="pt-4">
            <Button
              size="small"
              color="error"
              startIcon={<LinkOffIcon />}
              onClick={() => setDisconnectDialogOpen(true)}
              style={{ opacity: 0.6 }}
            >
              Desconectar HiperPay
            </Button>
          </div>

          <div className="border-t border-gray-300 border-opacity-30 pt-6">
            <BannerManager
              clientId={hiperpayClientId}
              clientSecret={hiperpayClientSecret}
            />
          </div>
        </>
      )}

      {/* Form Dialog */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingProfile ? 'Editar Profile' : 'Novo Profile'}</DialogTitle>
        <DialogContent className="space-y-4 pt-4">
          <div className="space-y-3 pt-2">
            <TextFieldCustom label="Nome *" value={form.name} onChange={updateField('name')} />
            <TextFieldCustom label="Titulo da pagina" value={form.title} onChange={updateField('title')} />
            <TextFieldCustom label="Subtitulo" value={form.subtitle} onChange={updateField('subtitle')} />
            <TextFieldCustom label="URL do Logo" value={form.logoUrl} onChange={updateField('logoUrl')} />
            <TextFieldCustom label="Largura do Logo (ex: 180px)" value={form.logoWidth} onChange={updateField('logoWidth')} />
            <div className="flex items-center space-x-3">
              <TextFieldCustom label="Cor primaria" value={form.primaryColor} onChange={updateField('primaryColor')} />
              <input
                type="color"
                value={form.primaryColor || '#03adec'}
                onChange={(e) => setForm(prev => ({ ...prev, primaryColor: e.target.value }))}
                className="w-10 h-10 rounded cursor-pointer border-0"
              />
            </div>
            <TextFieldCustom label="Mensagem informativa" value={form.info} onChange={updateField('info')} multiline rows={2} />
            <TextFieldCustom label="Titulo do rodape" value={form.footerTitle} onChange={updateField('footerTitle')} />
            <TextFieldCustom label="Subtitulo do rodape" value={form.footerSubtitle} onChange={updateField('footerSubtitle')} />
            <FormControlLabel
              control={
                <Switch
                  checked={form.isDefault}
                  onChange={(e) => setForm(prev => ({ ...prev, isDefault: e.target.checked }))}
                  color="primary"
                />
              }
              label="Definir como profile padrao"
            />
          </div>

          {/* Preview */}
          {(form.title || form.logoUrl || form.primaryColor) && (
            <Paper elevation={0} className="p-4 mt-4 bg-gray-100 rounded">
              <Text className="text-xs font-bold opacity-50 mb-2">PREVIEW</Text>
              <div className="bg-white rounded shadow p-4" style={{ borderLeft: `6px solid ${form.primaryColor || '#03adec'}` }}>
                {form.logoUrl && (
                  <div className="flex justify-center mb-3">
                    <img
                      src={form.logoUrl}
                      alt="Logo"
                      style={{ width: form.logoWidth || '180px', maxHeight: '80px', objectFit: 'contain' }}
                      onError={(e) => { e.target.style.display = 'none' }}
                    />
                  </div>
                )}
                {form.title && <p className="font-medium text-lg text-gray-700">{form.title}</p>}
                {form.subtitle && <p className="text-gray-600 text-sm">{form.subtitle}</p>}
                {form.info && (
                  <div className="mt-2 p-2 bg-gray-600 rounded">
                    <p className="text-white text-xs">{form.info}</p>
                  </div>
                )}
                {(form.footerTitle || form.footerSubtitle) && (
                  <div className="mt-3 text-center">
                    {form.footerTitle && <p className="text-gray-700 font-bold text-sm">{form.footerTitle}</p>}
                    {form.footerSubtitle && <p className="text-gray-500 text-xs">{form.footerSubtitle}</p>}
                  </div>
                )}
              </div>
            </Paper>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Remover profile</DialogTitle>
        <DialogContent>
          <Text>
            Tem certeza que deseja remover o profile "{profileToDelete?.name}"?
            Links ja criados nao serao afetados.
          </Text>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Remover</Button>
        </DialogActions>
      </Dialog>

      {/* Disconnect Confirmation */}
      <Dialog open={disconnectDialogOpen} onClose={() => setDisconnectDialogOpen(false)}>
        <DialogTitle>Desconectar HiperPay</DialogTitle>
        <DialogContent>
          <Text>
            Ao desconectar, os profiles existentes serao mantidos no HiperPay mas nao serao mais utilizados por esta instancia.
            Links ja criados continuam funcionando.
          </Text>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisconnectDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleDisconnectHiperPay}>Desconectar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!message}
        autoHideDuration={4000}
        onClose={() => setMessage('')}
        message={message}
      />

      <TemplateGenerator
        open={templateGeneratorOpen}
        onClose={() => setTemplateGeneratorOpen(false)}
        profiles={profiles}
        profileMode={profileMode}
      />
    </div>
  )
}
